import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, ButtonProps } from '../Button/Button';
import { Spinner, SpinnerProps } from '../Spinner/Spinner';

interface Props {
  oauthPopupUrl?: string;
  fetchOauthPopupUrl?: () => Promise<string | undefined>;
  oauthSuccessMessageType?: string;
  onRefreshNeeded?: () => void;
  popupWidth?: number;
  popupHeight?: number;
  buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>;
  windowProps?: { target?: string; windowFeatures?: string | string[] };
  spinnerProps?: Pick<SpinnerProps, 'size' | 'width'>;
}

export const OAuthPopupActionButton: FunctionComponent<Props> = props => {
  const {
    oauthPopupUrl,
    fetchOauthPopupUrl,
    oauthSuccessMessageType = 'googleAccountConnected',
    onRefreshNeeded,
    popupWidth,
    popupHeight,
    children,
    buttonProps,
    windowProps,
    spinnerProps,
  } = props;
  const [popup, setPopup] = useState<Window | null>(null);
  const [popupUrl, setPopupUrl] = useState(oauthPopupUrl || '');

  useEffect(() => {
    if (!oauthPopupUrl) {
      (async () => {
        const oauthUrl = await fetchOauthPopupUrl?.();
        setPopupUrl(oauthUrl || '');
      })();
    }
  }, []);

  useEffect(() => {
    if (!popup) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(API_URL).origin) {
        return;
      }
      const { data } = event;
      if (data?.type === oauthSuccessMessageType) {
        popup.close();
        setPopup(null);
        onRefreshNeeded?.();
      }
    };
    window.addEventListener('message', onMessage);
    const checkIfPopupClosedInterval = setInterval(() => {
      if (popup.closed) {
        setPopup(null);
        onRefreshNeeded?.();
      }
    }, 1000);
    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(checkIfPopupClosedInterval);
    };
  }, [popup, oauthSuccessMessageType, onRefreshNeeded]);

  const launchPopup = () => {
    const popup = window.open(
      popupUrl,
      windowProps?.target || 'oauth_popup',
      `width=${popupWidth || 500},height=${popupHeight || 800},${
        Array.isArray(windowProps?.windowFeatures)
          ? windowProps?.windowFeatures?.join(',')
          : windowProps?.windowFeatures
      }`,
    );

    setPopup(popup);
  };

  return (
    <Button
      size="md"
      disabled={!!popup || !popupUrl}
      onClick={launchPopup}
      {...buttonProps}
    >
      {!!popup && <Spinner standalone {...spinnerProps} />}
      {children || 'Connect to Google'}
    </Button>
  );
};
